import type { MatchFacts } from './history.js'
import type { MarketRules, MatchParams } from './params.js'
import { firstScorerChance, playerCupsChance, topScorerChance } from './players.js'
import { MIN_PROBABILITY, type Chance } from './price.js'
import type { SimResult } from './simulate.js'

/** Antepost (spec 4): mercati sul torneo, non su una partita; si quotano e si liquidano in `outrights.ts`. */
export const OUTRIGHT_MARKETS = ['outright_winner', 'outright_finalist', 'outright_top_scorer'] as const
export type OutrightMarketKey = (typeof OUTRIGHT_MARKETS)[number]
export const MARKET_KEYS = [
  'winner', 'exact_score', 'redemption', 'handicap', 'total_cups', 'clean_sheet', 'first_cup', 'total_airballs', 'rearrangement', 'duration', 'top_scorer', 'player_cups', 'first_scorer',
  ...OUTRIGHT_MARKETS,
] as const
export const isOutrightMarket = (key: string): key is OutrightMarketKey => (OUTRIGHT_MARKETS as readonly string[]).includes(key)
export type MarketKey = (typeof MARKET_KEYS)[number]
/** Gruppi della pagina partita, nell'ordine dei filtri: il primo è quello di partenza. */
export const MARKET_GROUPS = ['main', 'cups', 'events', 'players'] as const
export type MarketGroup = (typeof MARKET_GROUPS)[number]
export type Outcome = { market: MarketKey; selection: string; line?: number; subjectId?: string }
export type Resolution = 'won' | 'lost' | 'void' | 'open'
export type MarketContext = { facts: MatchFacts; cups: number; completed: boolean; live: boolean }

/** Mercati a due esiti complementari per linea e giocatore: due esiti dello stesso evento sarebbero opposti. */
export const PAIR_MARKETS: ReadonlySet<MarketKey> = new Set<MarketKey>([
  'winner', 'redemption', 'handicap', 'total_cups', 'clean_sheet', 'first_cup', 'total_airballs', 'rearrangement', 'duration', 'player_cups',
])
/** Mercati il cui esito nomina un giocatore o una squadra (gli antepost): `subjectId` obbligatorio. */
export const SUBJECT_MARKETS: ReadonlySet<MarketKey> = new Set<MarketKey>(['top_scorer', 'player_cups', 'first_scorer', ...OUTRIGHT_MARKETS])

/** Stesso esito: mercato, scelta, linea e giocatore (assenti = null). */
export const sameOutcome = (a: Outcome, b: Outcome): boolean =>
  a.market === b.market && a.selection === b.selection && (a.line ?? null) === (b.line ?? null) && (a.subjectId ?? null) === (b.subjectId ?? null)

export type MarketDef = {
  key: MarketKey
  group: MarketGroup
  available: (rules: MarketRules, params: MatchParams) => boolean
  /** Esiti offerti in questo stato (le linee si scelgono attorno alla mediana simulata). */
  outcomes: (sim: SimResult, ctx: MarketContext) => Outcome[]
  chance: (sim: SimResult, outcome: Outcome) => Chance
  /** Stessa regola per la quotazione dal vivo e per la liquidazione: 'open' finché l'esito non è deciso. */
  resolve: (ctx: MarketContext, outcome: Outcome) => Resolution
  /** Chiuso anche se non deciso (il 1-2 durante il tiro del pareggio). */
  closed?: (ctx: MarketContext) => boolean
  /** Una linea si offre solo se entrambi i suoi esiti hanno quota (spec 4): se ne cade uno, cadono tutti e due. */
  pairedLines?: boolean
}

const HANDICAP_LINES = [-2.5, -1.5, 1.5, 2.5]
const PLAYER_CUP_LINES = [2, 3]
// 0,5 = "almeno un airball": over è il vecchio sì, under il no (il mercato sì/no è confluito qui).
const AIRBALL_LINES = [0.5, 1.5, 2.5]

const share = (sim: SimResult, test: (i: number) => boolean): number => {
  let n = 0
  for (let i = 0; i < sim.runs; i++) if (test(i)) n++
  return n / sim.runs
}
const win = (sim: SimResult, test: (i: number) => boolean): Chance => ({ win: share(sim, test), void: 0 })
const median = (values: ArrayLike<number>): number => {
  const sorted = Array.from(values).sort((a, b) => a - b)
  return sorted[Math.floor(sorted.length / 2)] ?? 0
}
/** `true`/`false` = deciso (sì/no), `null` = ancora aperto. */
const yesNo = (decided: boolean | null, selection: string): Resolution => (decided === null ? 'open' : (selection === 'yes') === decided ? 'won' : 'lost')
/** Over/under: `true` = sopra la linea, `false` = sotto, `null` = aperto. */
const overUnder = (over: boolean | null, selection: string): Resolution => (over === null ? 'open' : over === (selection === 'over') ? 'won' : 'lost')
const pair = (market: MarketKey, a: string, b: string, extra: Partial<Outcome> = {}): Outcome[] => [
  { market, selection: a, ...extra },
  { market, selection: b, ...extra },
]
const total = (f: MatchFacts) => f.homeScore + f.awayScore
const cupsOf = (f: MatchFacts, id: string) => (f.homePlayerCups[id] ?? 0) + (f.awayPlayerCups[id] ?? 0)
const parseScore = (selection: string) => selection.split('-').map(Number) as [number, number]
const playerOutcomes = (sim: SimResult, market: MarketKey): Outcome[] => sim.players.map((id) => ({ market, selection: id, subjectId: id }))
const playerCount = (params: MatchParams) => params.homePlayers.length + params.awayPlayers.length

/** Il catalogo: l'ordine è quello delle pagine. */
export const MARKETS: readonly MarketDef[] = [
  {
    key: 'winner',
    group: 'main',
    available: () => true,
    outcomes: () => pair('winner', 'home', 'away'),
    chance: (sim, o) => win(sim, (i) => (sim.homeScore[i] > sim.awayScore[i]) === (o.selection === 'home')),
    resolve: ({ facts, completed }, o) => (completed ? ((facts.homeScore > facts.awayScore) === (o.selection === 'home') ? 'won' : 'lost') : 'open'),
  },
  {
    key: 'exact_score',
    group: 'main',
    available: () => true,
    outcomes: (sim) => {
      const counts = new Map<string, number>()
      for (let i = 0; i < sim.runs; i++) {
        const key = `${sim.homeScore[i]}-${sim.awayScore[i]}`
        counts.set(key, (counts.get(key) ?? 0) + 1)
      }
      return [...counts]
        .filter(([, n]) => n / sim.runs >= MIN_PROBABILITY)
        .sort((a, b) => b[1] - a[1])
        .map(([selection]): Outcome => ({ market: 'exact_score', selection }))
    },
    chance: (sim, o) => {
      const [h, a] = parseScore(o.selection)
      return win(sim, (i) => sim.homeScore[i] === h && sim.awayScore[i] === a)
    },
    resolve: ({ facts, completed }, o) => {
      const [h, a] = parseScore(o.selection)
      // I punteggi crescono soltanto: superato uno dei due, l'esito è perso.
      if (facts.homeScore > h || facts.awayScore > a) return 'lost'
      if (!completed) return 'open'
      return facts.homeScore === h && facts.awayScore === a ? 'won' : 'lost'
    },
  },
  {
    key: 'redemption',
    group: 'main',
    available: (rules) => rules.redemption,
    outcomes: () => pair('redemption', 'yes', 'no'),
    chance: (sim, o) => win(sim, (i) => (sim.redemptions[i] > 0) === (o.selection === 'yes')),
    resolve: ({ facts, completed }, o) => yesNo(facts.redemptionsMade > 0 ? true : completed ? false : null, o.selection),
  },
  {
    key: 'handicap',
    group: 'cups',
    pairedLines: true,
    available: () => true,
    outcomes: () => HANDICAP_LINES.flatMap((line) => pair('handicap', 'home', 'away', { line })),
    chance: (sim, o) => win(sim, (i) => (sim.homeScore[i] - sim.awayScore[i] + o.line! > 0) === (o.selection === 'home')),
    resolve: ({ facts, completed }, o) => (completed ? ((facts.homeScore - facts.awayScore + o.line! > 0) === (o.selection === 'home') ? 'won' : 'lost') : 'open'),
  },
  {
    key: 'total_cups',
    group: 'cups',
    available: () => true,
    outcomes: (sim, { facts }) => {
      const totals = new Int16Array(sim.runs)
      for (let i = 0; i < sim.runs; i++) totals[i] = sim.homeScore[i] + sim.awayScore[i]
      const m = median(totals)
      return [m - 1.5, m - 0.5, m + 0.5].filter((line) => line > total(facts)).flatMap((line) => pair('total_cups', 'over', 'under', { line }))
    },
    chance: (sim, o) => win(sim, (i) => (sim.homeScore[i] + sim.awayScore[i] > o.line!) === (o.selection === 'over')),
    resolve: ({ facts, completed }, o) => overUnder(total(facts) > o.line! ? true : completed ? false : null, o.selection),
  },
  {
    key: 'clean_sheet',
    group: 'cups',
    available: () => true,
    outcomes: () => pair('clean_sheet', 'yes', 'no'),
    chance: (sim, o) => win(sim, (i) => (Math.min(sim.homeScore[i], sim.awayScore[i]) === 0) === (o.selection === 'yes')),
    resolve: ({ facts, completed }, o) =>
      yesNo(facts.homeScore > 0 && facts.awayScore > 0 ? false : completed ? Math.min(facts.homeScore, facts.awayScore) === 0 : null, o.selection),
  },
  {
    key: 'first_cup',
    group: 'events',
    available: () => true,
    outcomes: () => pair('first_cup', 'home', 'away'),
    chance: (sim, o) => win(sim, (i) => sim.firstCup[i] === (o.selection === 'home' ? 1 : -1)),
    resolve: ({ facts }, o) => (facts.firstCup === null ? 'open' : facts.firstCup === o.selection ? 'won' : 'lost'),
  },
  {
    key: 'total_airballs',
    group: 'events',
    pairedLines: true,
    available: (rules) => rules.airball,
    outcomes: () => AIRBALL_LINES.flatMap((line) => pair('total_airballs', 'over', 'under', { line })),
    chance: (sim, o) => win(sim, (i) => (sim.airballs[i] > o.line!) === (o.selection === 'over')),
    resolve: ({ facts, completed }, o) => overUnder(facts.airballs > o.line! ? true : completed ? false : null, o.selection),
  },
  {
    key: 'rearrangement',
    group: 'events',
    // Tolto dall'offerta su richiesta dell'utente: resta nel catalogo solo per liquidare eventuali giocate già fatte.
    available: () => false,
    outcomes: () => pair('rearrangement', 'yes', 'no'),
    chance: (sim, o) => win(sim, (i) => (sim.rearrangements[i] > 0) === (o.selection === 'yes')),
    resolve: ({ facts, completed }, o) => yesNo(facts.rearrangements > 0 ? true : completed ? false : null, o.selection),
  },
  {
    key: 'duration',
    group: 'events',
    available: () => true,
    // Le quote non vedono l'orologio di parete (stessa quota a GET e POST): a partita in corso la durata sarebbe sbagliata.
    closed: ({ live }) => live,
    outcomes: (sim, { facts }) => {
      const minutes = Math.max(1, Math.floor(median(sim.duration) / 60))
      return [minutes, minutes + 1].filter((line) => line * 60 > facts.seconds).flatMap((line) => pair('duration', 'over', 'under', { line }))
    },
    chance: (sim, o) => win(sim, (i) => (sim.duration[i] > o.line! * 60) === (o.selection === 'over')),
    resolve: ({ facts, completed }, o) => overUnder(facts.seconds > o.line! * 60 ? true : completed ? false : null, o.selection),
  },
  {
    key: 'top_scorer',
    group: 'players',
    available: (_rules, params) => playerCount(params) >= 2,
    outcomes: (sim) => playerOutcomes(sim, 'top_scorer'),
    chance: (sim, o) => topScorerChance(sim, o.subjectId ?? o.selection),
    resolve: ({ facts, completed }, o) => {
      if (!completed) return 'open'
      const all = [...Object.values(facts.homePlayerCups), ...Object.values(facts.awayPlayerCups)]
      const max = Math.max(0, ...all)
      if (max === 0 || cupsOf(facts, o.selection) !== max) return 'lost'
      // Pari merito: rimborso a quota 1 per chi è in testa insieme ad altri (spec 4).
      return all.filter((n) => n === max).length > 1 ? 'void' : 'won'
    },
  },
  {
    key: 'player_cups',
    group: 'players',
    available: (_rules, params) => playerCount(params) >= 1,
    outcomes: (sim) => sim.players.flatMap((id) => PLAYER_CUP_LINES.flatMap((line) => pair('player_cups', 'yes', 'no', { subjectId: id, line }))),
    chance: (sim, o) => {
      const atLeast = playerCupsChance(sim, o.subjectId!, o.line!)
      return { win: o.selection === 'yes' ? atLeast : 1 - atLeast, void: 0 }
    },
    resolve: ({ facts, completed }, o) => yesNo(cupsOf(facts, o.subjectId!) >= o.line! ? true : completed ? false : null, o.selection),
  },
  {
    key: 'first_scorer',
    group: 'players',
    available: (_rules, params) => playerCount(params) >= 1,
    outcomes: (sim) => playerOutcomes(sim, 'first_scorer'),
    chance: (sim, o) => ({ win: firstScorerChance(sim, o.subjectId ?? o.selection), void: 0 }),
    resolve: ({ facts }, o) => (facts.firstScorer === null ? 'open' : facts.firstScorer === o.selection ? 'won' : 'lost'),
  },
]
